import React from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { Select } from 'antd';
// Api
import { GET_TICKET_EVENT_HOST_STREAMS } from 'api/ticketing/queries';
// Types
import {
  GetTicketEventHostStreams,
  GetTicketEventHostStreamsVariables,
} from 'api/ticketing/types/GetTicketEventHostStreams';
import {
  SortDirection,
  StreamOrderBy,
  StreamStatus,
  TicketEventStatus,
} from 'api/graphql-global-types';
// Constants
import { shortDateTimeFormat } from 'constants/global';

type StreamSelectProps = {
  hostId?: string;
  value?: string | null;
  onChange?: (value?: string | null) => void;
};

/** The host's scheduled streams that don't sell tickets yet. */
const StreamSelect = ({
  hostId,
  value,
  onChange,
}: StreamSelectProps): JSX.Element => {
  const { data, loading } = useQuery<
    GetTicketEventHostStreams,
    GetTicketEventHostStreamsVariables
  >(GET_TICKET_EVENT_HOST_STREAMS, {
    variables: {
      input: {
        storeId: hostId,
        streamStatus: [StreamStatus.Scheduled],
        orderBy: StreamOrderBy.ScheduleDate,
        direction: SortDirection.ASC,
        limit: 50,
      },
    },
    skip: !hostId,
    fetchPolicy: 'cache-and-network',
  });

  const streams = (data?.adminStreams?.entities || []).filter(
    (stream) =>
      stream &&
      (!stream.ticketEvent ||
        stream.ticketEvent.status === TicketEventStatus.Cancelled)
  );

  return (
    <Select
      allowClear
      showSearch
      value={value || undefined}
      onChange={(next?: string) => onChange?.(next || null)}
      loading={loading}
      disabled={!hostId}
      optionFilterProp="label"
      placeholder={
        hostId ? 'No stream: a standalone event' : 'Pick the host first'
      }
      aria-label="Stream"
      notFoundContent="This host has no scheduled stream without tickets"
      options={streams.map((stream) => ({
        value: stream?.id,
        label: `${stream?.name} · ${moment(stream?.scheduleDate).format(
          shortDateTimeFormat
        )}`,
      }))}
    />
  );
};

export default StreamSelect;
